import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Download, Plus, Pencil, Search } from "lucide-react";
import { supabase, type Existencia, type Zona } from "@/lib/supabase";
import { useAlmacen } from "@/lib/almacen";
import { useAuth } from "@/auth/AuthProvider";
import { useRpc, CLAVES_DOMINIO } from "@/lib/useRpc";
import { mapearErrorSupabase } from "@/lib/errores";
import { cantidad, num, hace, etiquetaEstado } from "@/lib/formato";
import { Tarjeta, Boton, Badge, Esqueleto, Vacio, Input, Select, Campo, Dialogo, ErrorBloque, tonoEstado } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { exportarCsv } from "./exportar";
import { filtroBusqueda } from "@/features/movimientos/BuscadorSku";
import { traerTodo } from "@/lib/paginado";

const POR_PAGINA = 50;

export function Inventario() {
  const { almacen, multi } = useAlmacen();
  const { esAdmin, perfil } = useAuth();
  const [q, setQ] = useState("");
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(0);
  const [filtro, setFiltro] = useState<"todos" | "alertas" | "sin_minimo" | "inactivos">("todos");
  const [editar, setEditar] = useState<Existencia | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const modoErp = perfil?.empresa.modo_ventas === "erp";

  useEffect(() => { const t = window.setTimeout(() => { setBusca(q.trim()); setPagina(0); }, 300); return () => window.clearTimeout(t); }, [q]);

  const lista = useQuery({
    queryKey: ["existencias", "inventario", almacen?.id, busca, pagina, filtro],
    enabled: !!almacen,
    queryFn: async () => {
      let query = supabase.from("v_existencias").select("*", { count: "exact" }).eq("almacen_id", almacen!.id).eq("activo", filtro !== "inactivos");
      if (busca) query = query.or(filtroBusqueda(busca));
      if (filtro === "alertas") query = query.in("estado", ["agotado", "bajo"]);
      if (filtro === "sin_minimo") query = query.eq("estado", "sin_minimo");
      const { data, error, count } = await query.order("sku").range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1);
      if (error) throw error;
      return { filas: data, total: count ?? 0 };
    },
  });

  const pendientes = useQuery({
    queryKey: ["pendientes"],
    enabled: esAdmin,
    queryFn: async () => { const { data, error } = await supabase.from("pendientes_catalogo").select("*").eq("resuelto", false).order("fecha", { ascending: false }); if (error) throw error; return data; },
  });

  async function exportar() {
    if (!almacen) return;
    const data = await traerTodo((desde, hasta) => supabase.from("v_existencias").select("*").eq("almacen_id", almacen.id).eq("activo", true).order("sku").range(desde, hasta));
    exportarCsv(`inventario-${almacen.codigo}-${new Date().toISOString().slice(0, 10)}.csv`, data, modoErp);
  }

  if (!almacen) return <Esqueleto />;
  const total = lista.data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="text-2xl font-bold">Inventario</h1><p className="text-sm text-tinta-suave">{multi ? almacen.nombre : "Físico, mínimos y rotación de cada producto."}</p></div>
        <div className="flex gap-2">
          <Boton variante="secundario" onClick={exportar}><Download className="h-4 w-4" /> Exportar CSV</Boton>
          {esAdmin && <Boton onClick={() => setNuevo(true)}><Plus className="h-4 w-4" /> Nuevo SKU</Boton>}
        </div>
      </div>

      {esAdmin && (pendientes.data?.length ?? 0) > 0 && (
        <div className="rounded-md border border-amber-200 bg-alerta-suave px-4 py-3 text-sm text-amber-900">
          <strong>{pendientes.data!.length} aviso(s) del almacén</strong> de productos que no están en el catálogo: {pendientes.data!.slice(0, 3).map((p) => p.sku_texto).join(", ")}{pendientes.data!.length > 3 ? "…" : ""}. <Link href="/ajustes" className="underline">Ver en Ajustes</Link>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-64 flex-1">
          <Search className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por clave, nombre o ubicación" className="pl-10" aria-label="Buscar" />
        </div>
        <Select value={filtro} onChange={(e) => { setFiltro(e.target.value as never); setPagina(0); }} className="w-auto" aria-label="Filtro">
          <option value="todos">Todos</option>
          <option value="alertas">Agotados y bajo mínimo</option>
          <option value="sin_minimo">Sin mínimo</option>
          <option value="inactivos">Inactivos</option>
        </Select>
      </div>

      {lista.isLoading ? <Esqueleto filas={8} /> : lista.error ? <ErrorBloque mensaje={mapearErrorSupabase(lista.error).mensaje} reintentar={() => lista.refetch()} /> : total === 0 ? (
        <Tarjeta>{busca || filtro !== "todos" ? <Vacio titulo={`Nada con “${busca || etiquetaFiltro(filtro)}”`} texto="Prueba otra clave o quita el filtro." /> :
          <Vacio titulo="Importa tu catálogo" texto="Sube el Excel de tu inventario y en minutos tienes todo aquí. O da de alta un SKU a la vez." accion={<div className="flex gap-2">{esAdmin && <Link href="/importar"><Boton><Plus className="h-4 w-4" /> Importar mi Excel</Boton></Link>}{esAdmin && <Boton variante="secundario" onClick={() => setNuevo(true)}>Dar de alta un SKU</Boton>}</div>} />}</Tarjeta>
      ) : (
        <>
          {/* Escritorio: tabla ≤ 6 columnas */}
          <div className="hidden overflow-x-auto rounded-md border border-borde bg-white md:block">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-tinta-suave">
                <tr>
                  <th className="px-3 py-2">Estado</th><th className="px-3 py-2">Producto</th><th className="px-3 py-2 text-right">Físico</th>
                  <th className="px-3 py-2 text-right">Mínimo</th>
                  <th className="px-3 py-2 text-right">{modoErp ? "Sistema / dif." : "Comprar"}</th>
                  <th className="px-3 py-2">{modoErp ? "Último conteo" : "Cobertura"}</th>
                  {esAdmin && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-borde">
                {lista.data!.filas.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2"><Badge tono={tonoEstado(e.estado)}>{etiquetaEstado(e.estado)}</Badge>{e.alerta_rotacion === "se_acaba" && <Badge tono="error" className="ml-1">se acaba en {num(e.dias_cobertura)} d</Badge>}</td>
                    <td className="px-3 py-2"><div className="font-medium">{e.sku}</div><div className="text-tinta-suave">{e.nombre}{e.ubicacion ? ` · ${e.ubicacion}` : ""}</div></td>
                    <td className="tabular px-3 py-2 text-right font-semibold">{cantidad(e.cantidad_fisica, e.unidad)}</td>
                    <td className="tabular px-3 py-2 text-right">{num(e.stock_minimo)}</td>
                    <td className="tabular px-3 py-2 text-right">{modoErp ? (e.existencia_erp == null ? "—" : <>{num(e.existencia_erp)} <span className={Number(e.diferencia_erp) < 0 ? "text-red-700" : Number(e.diferencia_erp) > 0 ? "text-emerald-700" : "text-tinta-suave"}>({Number(e.diferencia_erp) > 0 ? "+" : ""}{num(e.diferencia_erp)})</span></>) : (Number(e.sugerencia_compra) > 0 ? num(e.sugerencia_compra) : "—")}</td>
                    <td className="px-3 py-2 text-tinta-suave">{modoErp ? (e.ultimo_conteo_en ? `${hace(e.ultimo_conteo_en)} · ${e.ultimo_conteo_por_nombre ?? ""}` : "sin contar") : (e.dias_cobertura != null ? `${num(e.dias_cobertura)} días` : "sin rotación")}</td>
                    {esAdmin && <td className="px-3 py-2 text-right"><button onClick={() => setEditar(e)} className="rounded p-1.5 hover:bg-gray-100" aria-label={`Editar ${e.sku}`}><Pencil className="h-4 w-4" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Móvil: tarjetas */}
          <ul className="space-y-2 md:hidden">
            {lista.data!.filas.map((e) => (
              <li key={e.id} className="rounded-md border border-borde bg-white p-3" onClick={() => esAdmin && setEditar(e)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="font-medium">{e.sku}</div><div className="truncate text-sm text-tinta-suave">{e.nombre}</div></div>
                  <div className="tabular text-right"><div className="text-xl font-bold">{num(e.cantidad_fisica)}</div><div className="text-xs text-tinta-suave">{e.unidad} · mín {num(e.stock_minimo)}</div></div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-tinta-suave">
                  <Badge tono={tonoEstado(e.estado)}>{etiquetaEstado(e.estado)}</Badge>
                  {e.alerta_rotacion === "se_acaba" && <Badge tono="error">se acaba en {num(e.dias_cobertura)} d</Badge>}
                  <span>{e.ultimo_conteo_en ? `contado ${hace(e.ultimo_conteo_en)}` : "sin contar"}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between text-sm text-tinta-suave">
            <span>{total} producto{total === 1 ? "" : "s"}{busca ? ` con “${busca}”` : ""}</span>
            {paginas > 1 && (
              <span className="flex items-center gap-2">
                <Boton variante="secundario" tamano="sm" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>Anterior</Boton>
                {pagina + 1} / {paginas}
                <Boton variante="secundario" tamano="sm" disabled={pagina + 1 >= paginas} onClick={() => setPagina((p) => p + 1)}>Siguiente</Boton>
              </span>
            )}
          </div>
        </>
      )}

      {editar && <EditarExistencia e={editar} onCerrar={() => setEditar(null)} />}
      {nuevo && <NuevoProducto almacenId={almacen.id} onCerrar={() => setNuevo(false)} />}
    </div>
  );
}

function etiquetaFiltro(f: string) { return f === "alertas" ? "alertas" : f === "inactivos" ? "inactivos" : "sin mínimo"; }

function EditarExistencia({ e, onCerrar }: { e: Existencia; onCerrar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState(e.nombre ?? "");
  const [unidad, setUnidad] = useState(e.unidad ?? "PZA");
  const [minimo, setMinimo] = useState(String(e.stock_minimo ?? 0));
  const [consumo, setConsumo] = useState(e.consumo_esperado == null ? "" : String(e.consumo_esperado));
  const [periodo, setPeriodo] = useState<string>(String(e.periodo_dias ?? 30));
  const [ubicacion, setUbicacion] = useState(e.ubicacion ?? "");
  const [zonaId, setZonaId] = useState(e.zona_id ?? "");
  const [activo, setActivo] = useState(!!e.activo);
  const [guardando, setGuardando] = useState(false);
  const zonas = useQuery({ queryKey: ["zonas", "almacen", e.almacen_id], queryFn: async () => { const { data, error } = await supabase.from("zonas").select("*").eq("almacen_id", e.almacen_id!).order("codigo"); if (error) throw error; return data as Zona[]; } });

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    setGuardando(true);
    const min = Number(minimo.replace(",", "."));
    const cons = consumo.trim() === "" ? null : Number(consumo.replace(",", "."));
    const r1 = await supabase.from("productos").update({ nombre: nombre.trim(), unidad: unidad.trim().toUpperCase() || "PZA", activo }).eq("id", e.producto_id!);
    const r2 = await supabase.from("existencias").update({
      stock_minimo: Number.isFinite(min) && min >= 0 ? min : 0,
      consumo_esperado: cons != null && Number.isFinite(cons) && cons >= 0 ? cons : null,
      periodo_dias: cons != null && consumo.trim() !== "" ? Number(periodo) : null,
      ubicacion: ubicacion.trim() || null, zona_id: zonaId || null,
    }).eq("id", e.id!);
    setGuardando(false);
    const err = r1.error ?? r2.error;
    if (err) { toast.error(mapearErrorSupabase(err).mensaje); return; }
    await Promise.all(CLAVES_DOMINIO.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    toast.exito("Guardado");
    onCerrar();
  }

  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={`Editar ${e.sku}`}>
      <form onSubmit={guardar} className="space-y-3">
        <Campo etiqueta="Nombre" htmlFor="ed-nombre"><Input id="ed-nombre" value={nombre} onChange={(x) => setNombre(x.target.value)} required /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Unidad" htmlFor="ed-unidad"><Input id="ed-unidad" value={unidad} onChange={(x) => setUnidad(x.target.value)} /></Campo>
          <Campo etiqueta="Mínimo" htmlFor="ed-min" ayuda="Debajo de esto, alerta."><Input id="ed-min" inputMode="decimal" value={minimo} onChange={(x) => setMinimo(x.target.value)} className="tabular" /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Rotación esperada" htmlFor="ed-cons" ayuda="Cuánto se mueve normalmente."><Input id="ed-cons" inputMode="decimal" value={consumo} onChange={(x) => setConsumo(x.target.value)} placeholder="Ej. 300" className="tabular" /></Campo>
          <Campo etiqueta="Cada" htmlFor="ed-per"><Select id="ed-per" value={periodo} onChange={(x) => setPeriodo(x.target.value)}><option value="30">30 días (mes)</option><option value="90">90 días (trimestre)</option></Select></Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Zona" htmlFor="ed-zona"><Select id="ed-zona" value={zonaId} onChange={(x) => setZonaId(x.target.value)}><option value="">Sin zona</option>{zonas.data?.map((z) => <option key={z.id} value={z.id}>{z.codigo} · {z.nombre}</option>)}</Select></Campo>
          <Campo etiqueta="Ubicación" htmlFor="ed-ubi"><Input id="ed-ubi" value={ubicacion} onChange={(x) => setUbicacion(x.target.value)} placeholder="A-01-03" /></Campo>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={activo} onChange={(x) => setActivo(x.target.checked)} /> Producto activo</label>
        <div className="flex gap-2 pt-2"><Boton type="submit" cargando={guardando}>Guardar</Boton><Boton type="button" variante="secundario" onClick={onCerrar}>Cancelar</Boton></div>
      </form>
    </Dialogo>
  );
}

function NuevoProducto({ almacenId, onCerrar }: { almacenId: string; onCerrar: () => void }) {
  const toast = useToast();
  const [sku, setSku] = useState(""); const [nombre, setNombre] = useState(""); const [unidad, setUnidad] = useState("PZA");
  const [minimo, setMinimo] = useState("0"); const [ubicacion, setUbicacion] = useState(""); const [consumo, setConsumo] = useState(""); const [periodo, setPeriodo] = useState("30");
  const crear = useRpc("crear_producto", { onSuccess: (p) => { toast.exito(`${(p as { sku: string }).sku} creado. Regístrale una entrada para darle existencia.`); onCerrar(); } });
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo="Nuevo producto">
      <form onSubmit={(e) => { e.preventDefault(); crear.mutate({ p_sku: sku, p_nombre: nombre, p_unidad: unidad, p_almacen_id: almacenId, p_ubicacion: ubicacion || undefined, p_stock_minimo: Number(minimo.replace(",", ".")) || 0, p_consumo_esperado: consumo.trim() ? Number(consumo.replace(",", ".")) : undefined, p_periodo_dias: consumo.trim() ? Number(periodo) : undefined }); }} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Clave (SKU)" htmlFor="nu-sku"><Input id="nu-sku" value={sku} onChange={(x) => setSku(x.target.value)} required placeholder="CAB-1234" /></Campo>
          <Campo etiqueta="Unidad" htmlFor="nu-uni"><Input id="nu-uni" value={unidad} onChange={(x) => setUnidad(x.target.value)} placeholder="PZA, M, CAJA" /></Campo>
        </div>
        <Campo etiqueta="Nombre" htmlFor="nu-nom"><Input id="nu-nom" value={nombre} onChange={(x) => setNombre(x.target.value)} required /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Mínimo" htmlFor="nu-min"><Input id="nu-min" inputMode="decimal" value={minimo} onChange={(x) => setMinimo(x.target.value)} className="tabular" /></Campo>
          <Campo etiqueta="Ubicación" htmlFor="nu-ubi" ayuda="A-01-03 → zona A"><Input id="nu-ubi" value={ubicacion} onChange={(x) => setUbicacion(x.target.value)} /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo etiqueta="Rotación esperada" htmlFor="nu-cons"><Input id="nu-cons" inputMode="decimal" value={consumo} onChange={(x) => setConsumo(x.target.value)} placeholder="Opcional" className="tabular" /></Campo>
          <Campo etiqueta="Cada" htmlFor="nu-per"><Select id="nu-per" value={periodo} onChange={(x) => setPeriodo(x.target.value)}><option value="30">30 días</option><option value="90">90 días</option></Select></Campo>
        </div>
        <p className="text-sm text-tinta-suave">El producto nace con físico 0; la existencia se da con una Entrada o un conteo.</p>
        <div className="flex gap-2 pt-2"><Boton type="submit" cargando={crear.isPending}>Crear</Boton><Boton type="button" variante="secundario" onClick={onCerrar}>Cancelar</Boton></div>
      </form>
    </Dialogo>
  );
}
